import { Component, ComponentType, ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import {
  HiOutlineExclamationCircle,
  HiChevronDown,
  HiChevronRight,
} from 'react-icons/hi';
import styled from 'styled-components';

import { FillButton, Icon, Stack, Text } from '~uikit';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  showDetails: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    componentStack: null,
    showDetails: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    // No external error reporting — surface details in the UI (and the console
    // for local debugging) instead.
    this.setState({ error, componentStack: errorInfo?.componentStack ?? null });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    const { error, componentStack, showDetails } = this.state;
    const { children } = this.props;

    if (!error) return children;

    const details = [
      error.message ? `${error.name}: ${error.message}` : error.name,
      error.stack,
      componentStack ? `Component stack:${componentStack}` : null,
    ]
      .filter(Boolean)
      .join('\n\n');

    return (
      <Wrapper>
        <Card axis="y" spacing="normal" align="center">
          <Icon icon={HiOutlineExclamationCircle} size={44} color="errorText" />
          <Text variant="title3">
            <Trans>Something went wrong</Trans>
          </Text>
          <Text variant="body" color="muted1" style={{ textAlign: 'center' }}>
            <Trans>
              This part of the app ran into an unexpected error. Try reloading
              the page — if it keeps happening, the details below may help.
            </Trans>
          </Text>

          <FillButton
            variant="primary"
            onClick={() => window.location.reload()}
          >
            <Trans>Reload page</Trans>
          </FillButton>

          {details && (
            <Details>
              <DetailsToggle
                type="button"
                aria-expanded={showDetails}
                onClick={() =>
                  this.setState(s => ({ showDetails: !s.showDetails }))
                }
              >
                <Icon
                  icon={showDetails ? HiChevronDown : HiChevronRight}
                  size="small"
                  color="muted1"
                />
                <Text variant="bodySmallStrong" color="muted1">
                  {showDetails ? (
                    <Trans>Hide details</Trans>
                  ) : (
                    <Trans>Show details</Trans>
                  )}
                </Text>
              </DetailsToggle>
              {showDetails && <Pre>{details}</Pre>}
            </Details>
          )}
        </Card>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${p => p.theme.spacing.large}px ${p => p.theme.spacing.medium}px;
`;

const Card = styled(Stack)`
  width: 100%;
  max-width: 560px;
`;

const Details = styled.div`
  align-self: stretch;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radii.small}px;
  background: ${p => p.theme.colors.muted5};
  overflow: hidden;
`;

const DetailsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xsmall}px;
  width: 100%;
  padding: ${p => p.theme.spacing.small}px ${p => p.theme.spacing.normal}px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
`;

const Pre = styled.pre`
  margin: 0;
  padding: ${p => p.theme.spacing.normal}px;
  border-top: 1px solid ${p => p.theme.colors.border};
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${p => p.theme.colors.muted1};
`;

export function withErrorBoundary<P extends Props>(Comp: ComponentType<P>) {
  const displayName = Comp.displayName || Comp.name || 'Component';

  return class extends Component<Props> {
    public static displayName = `withErrorBoundary(${displayName})`;

    render() {
      return (
        <ErrorBoundary>
          <Comp {...(this.props as P)} />
        </ErrorBoundary>
      );
    }
  };
}
